/**
 * Sewing Efficiency — the inline daily trend under the summary bar chart.
 *
 * No second query and no new grain: it runs the report's existing `daily` view with `line` set to
 * the clicked line, fetched only once a bar is clicked (§4.7). `hasTarget` is a runtime check
 * against the response rather than an assumption, so the target series drops cleanly if a view
 * ever stops returning it.
 *
 * Legacy's per-timeslot Reasons and Corrective Measures are deliberately not built: they are empty
 * in the source, and an always-blank field reads as "nothing went wrong" (`improvements.md`).
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { Area, AreaChart, LabelList, XAxis, YAxis } from 'recharts';
import { format, isValid, parseISO } from 'date-fns';

import { ReportError } from '@/components/report/ReportError';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  chartColor,
  type ChartConfig,
} from '@/components/ui/chart';
import { decimal, integer } from '@/components/report/groupedColumns';
import { useReportQuery } from '@/hooks/useReportQuery';
import type { ReportQueryRequest } from '@/lib/api';

import { plottedFields, type SewingRow } from './columns';
import type { ChartMode, Metric } from './filters';

const REPORT_ID = 'sewing-efficiency';
const AXIS_DATE = 'd MMM';

interface Point {
  date: string;
  label: string;
  actual: number | null;
  target: number | null;
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Fractions on the wire, percentages on the axis — the same scaling the table and bars apply. */
function scaled(value: unknown, isPercent: boolean): number | null {
  const parsed = toNumber(value);
  return parsed === null ? null : isPercent ? parsed * 100 : parsed;
}

function axisLabel(raw: unknown): string {
  const text = String(raw ?? '');
  const parsed = parseISO(text);
  return isValid(parsed) ? format(parsed, AXIS_DATE) : text;
}

function formatValue(value: number, isPercent: boolean): string {
  return isPercent ? `${decimal.format(value)}%` : integer.format(Math.round(value));
}

/** Room one point needs before it earns a marker: an 8px dot plus 6px of air. */
const DOT_MIN_SLOT = 14;

/** Room a printed value needs: ~38px for the widest figure at `text-[10px]`, plus its gap. */
const LABEL_MIN_SLOT = 46;

/** Below this there is no plot worth measuring — the panel is mid-layout and nothing is drawn. */
const MIN_PLOT_WIDTH = 80;

interface Density {
  /** A marker on every measured day, or none at all. */
  showDots: boolean;
  /** Label every Nth point. `0` labels none — too dense, or the plot is not measured yet. */
  labelStride: number;
}

/**
 * What this many points can carry in this much width. A computed stride, not a collision test:
 * Recharts can only tell a label where it sits, not what its neighbours are doing, so hiding after
 * layout flickers on resize and drops labels arbitrarily.
 */
function densityFor(pointCount: number, plotWidth: number): Density {
  if (pointCount === 0 || plotWidth < MIN_PLOT_WIDTH) {
    return { showDots: false, labelStride: 0 };
  }
  const slot = plotWidth / pointCount;
  const stride = Math.ceil(LABEL_MIN_SLOT / slot);
  return {
    showDots: slot >= DOT_MIN_SLOT,
    labelStride: stride > 10 ? 0 : stride,
  };
}

/**
 * The rendered width of an element, kept current across resize. **A callback ref, not `useRef` +
 * `useEffect`**: the chart node does not exist on the mount an effect with `[]` deps runs on, so
 * the width would stay 0 for the life of the panel and every dot and label would silently vanish.
 */
function useMeasuredWidth<T extends HTMLElement>() {
  const [width, setWidth] = useState(0);
  const observer = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: T | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (node === null) {
      return;
    }
    // Measured up front as well as on change: a ResizeObserver fires on the next frame, so the
    // first paint would otherwise draw a chart that has decided it has no room.
    setWidth(node.getBoundingClientRect().width);
    const next = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry !== undefined) {
        setWidth(entry.contentRect.width);
      }
    });
    next.observe(node);
    observer.current = next;
  }, []);

  return { ref, width };
}

export interface LineTrendProps {
  /** The `line_no` of the clicked bar — a server code, used as the `line` filter. */
  lineNo: string;
  /** The line's own name, for the heading. */
  lineLabel: string;
  /**
   * The report's current filters, with `line` replaced by the clicked line. **Not a slice of the
   * same answer as the panels around it**: those draw from `summary`, which drops a line's
   * no-output days, while `daily` keeps every day and returns null for a family that recorded
   * nothing — which draws here as a gap in the line.
   */
  filters: ReportQueryRequest['filters'];
  mode: ChartMode;
  metric: Metric;
  includePpa: boolean;
  staleTime: number;
  refetchInterval: number | undefined;
  onClose: () => void;
}

export function LineTrend({
  lineNo,
  lineLabel,
  filters,
  mode,
  metric,
  includePpa,
  staleTime,
  refetchInterval,
  onClose,
}: LineTrendProps) {
  const trend = useReportQuery<SewingRow>(
    REPORT_ID,
    { ...filters, line: lineNo },
    {
      view: 'daily',
      staleTime,
      ...(refetchInterval === undefined ? {} : { refetchInterval }),
    },
  );

  const response = trend.data;
  const fields = useMemo(() => {
    const available = new Set((response?.columns ?? []).map((column) => column.field));
    const plotted = plottedFields(mode, metric, includePpa, available);
    // Checked against the response rather than assumed, so this corrects itself either way.
    return { ...plotted, hasTarget: available.has(plotted.target) };
  }, [response, mode, metric, includePpa]);

  const hasTarget = fields.hasTarget;

  // Memoized: Recharts restarts its line-draw animation whenever `data` changes identity, so an
  // array rebuilt per render leaves the chart perpetually mid-animation.
  const data = useMemo<Point[]>(
    () =>
      (response?.rows ?? []).map((row) => ({
        date: String(row['tran_date'] ?? ''),
        label: axisLabel(row['tran_date']),
        actual: scaled(row[fields.actual], fields.isPercent),
        target: hasTarget ? scaled(row[fields.target], fields.isPercent) : null,
      })),
    [response, fields, hasTarget],
  );

  // Same reason: `<ChartContainer>` puts this straight into a context value.
  const config = useMemo<ChartConfig>(
    () => ({
      actual: { label: fields.actualLabel, color: 'chart-1' },
      ...(hasTarget ? { target: { label: fields.targetLabel, color: 'muted-foreground' } } : {}),
    }),
    [fields, hasTarget],
  );

  const heading = `${lineLabel} — day by day`;

  // The plot's own width, not the panel's: the axis and margin are not plottable, and counting
  // them over-estimates the room a label has by ~56px.
  const { ref: plotRef, width: panelWidth } = useMeasuredWidth<HTMLDivElement>();
  const axisWidth = fields.isPercent ? 48 : 60;
  const { showDots, labelStride } = densityFor(data.length, panelWidth - axisWidth - 16);

  /** Which points print their value. Anchored on the **last** point, so the most recent day always
   * carries a number however the stride falls. */
  const labelledIndex = (index: number): boolean =>
    labelStride > 0 && (data.length - 1 - index) % labelStride === 0;

  return (
    <section
      aria-label={heading}
      className="flex flex-col gap-1.5 rounded-lg border bg-card p-2.5"
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-medium">{heading}</h3>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 px-2 text-xs"
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faXmark} className="size-3" aria-hidden />
          Close
        </Button>
      </div>

      {/* Its own compact states: `<ReportPage>`'s shell describes the active view (§4.2) and must
          not be taken over by a panel opened inside it. */}
      {trend.error !== null ? (
        <ReportError error={trend.error} />
      ) : trend.isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : data.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          This line recorded no days in the selected period. Widen the dates to see its trend.
        </p>
      ) : (
        <ChartContainer
          config={config}
          aria-label={`${fields.title} by day for ${lineLabel}`}
          className="aspect-auto h-52 w-full"
          ref={plotRef}
        >
          {/* top: 18 rather than 8 — Recharts clips a top label at the plot edge. */}
          <AreaChart
            data={data}
            accessibilityLayer
            margin={{ top: labelStride > 0 ? 18 : 8, right: 8, bottom: 0 }}
          >
            <XAxis dataKey="label" tickLine={false} axisLine={false} minTickGap={16} />
            <YAxis
              width={fields.isPercent ? 48 : 60}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value: number) =>
                fields.isPercent
                  ? `${String(Math.round(value))}%`
                  : integer.format(Math.round(value))
              }
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  formatter={(value) =>
                    typeof value === 'number' ? formatValue(value, fields.isPercent) : String(value)
                  }
                />
              }
            />
            <Area
              dataKey="actual"
              type="monotone"
              stroke={chartColor('chart-1')}
              strokeWidth={2}
              fill={chartColor('chart-1')}
              fillOpacity={0.2}
              // A function, because that is the only way to withhold one point's mark: Recharts
              // otherwise draws a null point's dot at the baseline, claiming a measured zero on a
              // day that recorded nothing. Nulls must return an empty <g/> — returning null is not
              // a valid SVG element to Recharts and throws.
              dot={
                showDots
                  ? (props) => {
                      // No `key`: Recharts keys its own `<DotItem>` wrapper and passes geometry only.
                      const { cx, cy } = props as { cx?: number; cy?: number };
                      if (!Number.isFinite(cx) || !Number.isFinite(cy)) {
                        return <g />;
                      }
                      return (
                        <circle
                          cx={cx}
                          cy={cy}
                          r={4}
                          fill={chartColor('chart-1')}
                          stroke={chartColor('card')}
                          strokeWidth={2}
                        />
                      );
                    }
                  : false
              }
              // A day the line did not run is a gap, not a dip to zero.
              connectNulls={false}
            >
              {labelStride > 0 ? (
                <LabelList
                  position="top"
                  offset={8}
                  fill={chartColor('foreground')}
                  fontSize={10}
                  // NO `dataKey`, deliberately: Recharts ignores `valueAccessor` when one is set,
                  // and would print a number on every point. Returning `null` is what withholds a
                  // label — `''` is not nullish and leaves an empty element at every skipped point.
                  valueAccessor={(entry: { payload?: Point }, index: number) => {
                    if (!labelledIndex(index)) {
                      return null;
                    }
                    const value = entry.payload?.actual;
                    return typeof value === 'number' ? formatValue(value, fields.isPercent) : null;
                  }}
                />
              ) : null}
            </Area>
            {hasTarget ? (
              <Area
                dataKey="target"
                type="monotone"
                stroke={chartColor('muted-foreground')}
                strokeWidth={1.5}
                strokeDasharray="4 3"
                fill={chartColor('muted-foreground')}
                fillOpacity={0}
                dot={false}
                connectNulls={false}
              />
            ) : null}
          </AreaChart>
        </ChartContainer>
      )}

      {hasTarget ? null : (
        <p className="text-xs text-muted-foreground">
          The daily grain carries no efficiency target, so none is drawn. Switch the chart to Output
          to compare each day against its target.
        </p>
      )}
    </section>
  );
}
